import { Link } from "react-router-dom";
import { NerdStackFooter } from "../components/NerdStackFooter.jsx";
import { ToolCard } from "../components/ToolCard.jsx";
import { FolderGrid } from "../components/FolderGrid.jsx";
import BackArrow from "../components/BackArrow.jsx";
import { MathShell, MATH_ACCENT, VIOLET } from "./MathShell.jsx";

// V45.3 — MathHome is now a custom ChemHome-style layout (not a MathShell wrapper).
// MathShell stays imported for the *Landing* export and is still used by every
// other math tool page (Calculator, Quadratic, etc).
const EM = MATH_ACCENT;     // teal #2DD4BF
const EM_DEEP = VIOLET;     // family violet #7C3AED
const EM_LIGHT = "#A7F3D0"; // light teal for gradient-text

const rgba = (hex, a) => {
  const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
};

// MathLanding — the small card view used at /math (deferred-route landing).
// Preserved verbatim from pre-V45.3.
export function MathLanding() {
  return (
    <div style={{ minHeight: "100vh", background: "#0A0B14", color: "#E8E9F3", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "24px", textAlign: "center", paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>
      <div style={{ width: 72, height: 72, borderRadius: 18, background: `linear-gradient(135deg, ${MATH_ACCENT}, ${VIOLET})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 30, fontWeight: 700, color: "#fff", marginBottom: 20 }}>
        Ma
      </div>
      <h1 style={{ fontSize: 26, fontWeight: 700, margin: "0 0 8px" }}>NerdMath</h1>
      <p style={{ fontSize: 15, color: "#9A9DBE", margin: "0 0 28px", maxWidth: 320 }}>
        Scientific calculator, equation solvers, and reference. Free. Fast. Actually good.
      </p>
      <Link to="/math/home" style={{ background: MATH_ACCENT, color: "#06201C", fontWeight: 600, fontSize: 15, padding: "13px 28px", borderRadius: 12, textDecoration: "none" }}>
        Open NerdMath
      </Link>
    </div>
  );
}

export function MathHome() {
  return (
    <div style={{ minHeight: "100vh", background: "#0A0B14", fontFamily: "'DM Sans', system-ui, sans-serif", color: "#E2E8F0" }}>
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, height: "70%", background: `radial-gradient(ellipse at center bottom, ${rgba(EM, 0.18)} 0%, transparent 70%)`, pointerEvents: "none" }} />

      <header style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "16px 24px",
        paddingTop: "calc(16px + env(safe-area-inset-top))",
        paddingLeft: "calc(24px + env(safe-area-inset-left))",
        paddingRight: "calc(24px + env(safe-area-inset-right))",
        position: "relative", zIndex: 10,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <BackArrow to="/" from={EM} toColor={EM_LIGHT} />
          <div style={{ width: 38, height: 38, borderRadius: 11, background: `linear-gradient(135deg, ${EM}, ${EM_DEEP})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, fontWeight: 800, color: "#fff" }}>Ma</div>
          <svg width="105" height="24" viewBox="0 0 105 24" xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }}>
            <defs>
              <linearGradient id="wm-math" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor={EM_LIGHT} />
                <stop offset="1" stopColor="#5EEAD4" />
              </linearGradient>
            </defs>
            <text x="0" y="19" fill="url(#wm-math)" fontFamily="DM Sans, system-ui, sans-serif" fontSize="20" fontWeight="800" letterSpacing="-0.5">NerdMath</text>
          </svg>
        </div>
      </header>

      <main style={{ maxWidth: 820, margin: "0 auto", padding: "40px 20px 80px", position: "relative", zIndex: 10 }}>
        <h1 style={{ fontSize: "clamp(32px, 6vw, 52px)", fontWeight: 800, letterSpacing: "-1.5px", textAlign: "center", margin: "0 0 14px", lineHeight: 1.1 }}>
          Math tools.<br /><span style={{ background: `linear-gradient(135deg, ${EM_LIGHT}, ${EM})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Free. Fast. Actually good.</span>
        </h1>
        <p style={{ fontSize: 17, color: "#9CA3AF", textAlign: "center", maxWidth: 580, margin: "0 auto 14px", lineHeight: 1.6 }}>
          Math by feel, not flashcard. Scientific calculator, equation solvers, trig reference, matrices — every formula you'd reach for in a textbook, with worked steps.
        </p>
        <p style={{ fontSize: 15, color: "#6F7290", textAlign: "center", maxWidth: 580, margin: "0 auto 44px", lineHeight: 1.6 }}>
          No tracking. No signup. No dark patterns.
        </p>

        <FolderGrid ariaLabel="Math tools">
          <ToolCard accent={EM} to="/math/calculator" name="Scientific Calculator" desc="Full expression evaluator — trig, logs, powers, factorial" />
          <ToolCard accent={EM} to="/math/quadratic" name="Quadratic Solver" desc="Roots with full worked steps (free — Symbolab paywalls this)" />
          <ToolCard accent={EM} to="/math/trig" name="Trig & Unit Circle" desc="Exact and decimal values for any angle" />
          <ToolCard accent={EM} to="/math/logarithm" name="Logarithm" desc="Any base, natural log, common log" />
          <ToolCard accent={EM} to="/math/matrix" name="Matrix" desc="Determinant, inverse, and linear system solver" />
          <ToolCard accent={EM} to="/math/reference" name="Math Reference" desc="Algebra through PDEs — theorems, formulas & worked examples — with quiz" />
          <ToolCard accent={EM} to="/suggest" name="Suggest a tool" desc="Missing something? Tell us what to build next." />
        </FolderGrid>
      </main>

      <footer style={{ textAlign: "center", padding: 24, fontSize: 12, color: "#1F2937", borderTop: `1px solid ${rgba(EM, 0.04)}` }}>
        <NerdStackFooter variant="tool" family="math" />
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        body { margin: 0; }
      `}</style>
    </div>
  );
}
